import { useCallback, useEffect, useState } from 'react'

import { api, can, type ConnectorsView } from '../../lib/api'
import { ErrorBox, Loading } from '../../components/DataTable'
import { IntegrationCard, Status } from '../../components/kit'
import { ReasonTip } from '../../components/vendor/ReasonTip'
import { IntegrationDrawer } from '../../components/integrations/IntegrationDrawer'
import { CATEGORIES, actionWord, buildCatalogue, matches, type Filter } from '../../components/integrations/catalogue'
import '../../styles/integrations.css'

// The integrations catalogue.
//
// # WHY IT IS A CATALOGUE AND NOT A WALL OF LOGOS
//
// Because a logo implies a built integration and half of any such wall is
// aspiration. Somebody who presses one and finds nothing there stops trusting
// the rest. Every card here either is something this product can talk to, and
// says whether this tenant has connected it and whether it works, or says
// "Coming next" and offers no connection.
//
// # WHY BY JOB AND NOT BY VENDOR TYPE
//
// A person arrives with a job in mind: stop purchase orders to an uncleared
// supplier, prove a leaving vendor's accounts are closed, reach a vendor who
// does not read email. The categories are those jobs, in the order a third
// party risk programme meets them, and each card has one status and one action.
// Everything else (what it does step by step, the field mapping, setup, the
// test and the form) is in the drawer the card opens.
//
// # WHY SIGN-IN AND MAIL ARE STILL ON THEIR OWN TABS
//
// They are not integrations a customer chooses among: every deployment has
// exactly one of each and configuring it is a task rather than a selection.
// Mail and the Entra ID proof appear here as cards so nobody concludes they are
// missing, and those cards open the tab where they are configured.

const FILTERS: { key: Filter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'connected', label: 'Connected' },
  { key: 'attention', label: 'Needs attention' },
  { key: 'coming_next', label: 'Coming next' },
]

const EMPTY: Record<Filter, string> = {
  all: 'Nothing in the catalogue yet',
  connected: 'Nothing is connected yet',
  attention: 'Nothing needs attention',
  coming_next: 'Nothing is waiting to be built',
}

export function Integrations() {
  const [view, setView] = useState<ConnectorsView | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [filter, setFilter] = useState<Filter>('all')
  // The drawer keeps the last card it showed while it slides closed, so its
  // contents do not vanish half way through the exit.
  const [openId, setOpenId] = useState<string | null>(null)
  const [open, setOpen] = useState(false)

  const load = useCallback(() => {
    api
      .connectors()
      .then((v) => {
        setView(v)
        setError(null)
      })
      .catch(setError)
  }, [])
  useEffect(load, [load])

  if (error != null && !view) return <ErrorBox error={error} what="Integrations" />
  if (!view) return <Loading what="the catalogue" />

  const inherited = view.source === 'INHERIT'
  const mayWrite = can('config.write') && !inherited
  const cards = buildCatalogue(view)
  const count = (f: Filter) => cards.filter((c) => matches(c, f)).length
  const shown = cards.filter((c) => matches(c, filter))
  const selected = cards.find((c) => c.id === openId) ?? null

  return (
    <div className="settings-stack int-page">
      <section className="panel int-head">
        <div className="int-title">
          <h2>Integrations</h2>
          {inherited && (
            <span className="int-inherited">
              <Status tone="info">Inherited</Status>
              {/* The API's reason is shown whole: a toggletip when it fits one,
                  a drawer when it does not, never cut at a word. */}
              <ReasonTip label="Why these cannot be changed here" text={view.why} />
            </span>
          )}
        </div>
        <div className="int-seg" role="group" aria-label="Show integrations">
          {FILTERS.map((f) => (
            <button key={f.key} type="button" aria-pressed={filter === f.key} onClick={() => setFilter(f.key)}>
              {f.label} <b className="int-count">{count(f.key)}</b>
            </button>
          ))}
        </div>
        <p className="int-away">
          Sign-in is on <a href="#/settings?t=sso">Identity and SSO</a>, mail on <a href="#/settings?t=email">Email</a>
        </p>
        {error != null && <p className="note warn">The catalogue could not be refreshed. What is shown may be out of date</p>}
      </section>

      {shown.length === 0 && <p className="int-empty">{EMPTY[filter]}</p>}

      {CATEGORIES.map((cat) => {
        const inCat = shown.filter((c) => c.category === cat.key)
        if (inCat.length === 0) return null
        return (
          <section key={cat.key} className="int-cat" aria-labelledby={'int-cat-' + cat.key}>
            <div className="int-cat-head">
              <h3 id={'int-cat-' + cat.key}>{cat.name}</h3>
              <span>{cat.line}</span>
            </div>
            <div className="int-cards">
              {inCat.map((c) => (
                <IntegrationCard
                  key={c.id}
                  mark={c.mark}
                  markColor={c.markColor}
                  name={c.name}
                  line={c.line}
                  status={c.statusWord}
                  statusTone={c.tone}
                  action={actionWord(c, mayWrite)}
                  featured={c.featured}
                  onOpen={() => {
                    setOpenId(c.id)
                    setOpen(true)
                  }}
                />
              ))}
            </div>
          </section>
        )
      })}

      <IntegrationDrawer
        card={selected}
        open={open && selected != null}
        onClose={() => setOpen(false)}
        mayWrite={mayWrite}
        why={inherited ? 'Inherited from the platform, so it is changed there' : undefined}
        onChanged={load}
      />
    </div>
  )
}
